import { createSelector } from '@reduxjs/toolkit'
import { createSelectorCache, getOrBuildSelector } from '../../Sinks/Selector/SelectorCache.ts'
import type { KronaFilterOpts, TransformResult } from '../Types.ts'
import type { State } from '../../../../../Flux/Slice/index.ts'
import type { SourceId } from '../../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import { selectFilteredCube } from '../../Sinks/Selector/SelectFilteredCube.ts'
import { computeStackedBarRows } from '../../Sinks/Selector/TaxaStackedBarCore.ts'
import { forwardCubeStatus, selectCubeContext } from '../../Sinks/Selector/CubeContext.ts'
import { collapseStackedSeries } from '../../Sinks/Selector/CollapseSeries.ts'
import { isVizPerfEnabled } from '../../../../../Repository/Visualization/VizPerf.ts'

/**
 * A stacked segment thinner than this share of its bucket's total is sub-pixel on any realistic
 * chart height (0.5% of a ~300px plot ≈ 1.5px) — invisible, not in the (hidden) legend, and
 * effectively unclickable. Used only to *count* the long tail for the perf diagnostic below.
 */
const VISIBILITY_SHARE = 0.005

export type TaxaStackedBarSeries = {
  data: Array<Record<string, number | string>>
  stackKeys: string[]
  /** Samples per bucket, aligned to `data` by index. Beside the rows — see TaxaStackedBarCore. */
  sampleCounts: (number | undefined)[]
}

const selectorCache = createSelectorCache<TransformResult<TaxaStackedBarSeries>>()

export function selectTaxaStackedBarSeries(
  state: typeof State,
  sourceId: SourceId,
  _bucketKey: string | undefined,
  opts: KronaFilterOpts,
  optsKey: string
): TransformResult<TaxaStackedBarSeries> {
  const cacheId = `${sourceId}|${optsKey}`

  return getOrBuildSelector(selectorCache, cacheId, () =>
    createSelector(
      [
        (s: typeof State) => selectCubeContext(s, sourceId),
        (s: typeof State) =>
          selectFilteredCube(s, sourceId, { selectedPath: opts.selectedPath, gradeThreshold: opts.gradeThreshold })
      ],
      (ctx, filteredBuckets): TransformResult<TaxaStackedBarSeries> => {
        if (ctx.status !== 'ready') return forwardCubeStatus(ctx)

        const isPct = ctx.scalingMode === 'PERCENTAGE'

        // Pure series shaping lives in TaxaStackedBarCore so the SSR render sidecar and this selector
        // stay one source of truth. Uses the PRE-collapse rows here so the diagnostic below still
        // measures the full set before the long-tail "Other" collapse.
        const { data, stackKeys, sampleCounts } = computeStackedBarRows({
          filteredBuckets,
          level: opts.selectedPath.length,
          isPercent: isPct
        })

        if (isVizPerfEnabled()) reportSeriesVisibility(data, stackKeys, opts.selectedPath.length)

        // Collapse the unreadable long tail into "Other" (see CollapseSeries). Faithful: bar
        // totals/percentages are preserved. Diagnostic above runs on the FULL set so it still
        // reports how much was trimmed.
        const collapsed = collapseStackedSeries(data, stackKeys)

        return { status: 'ready', value: { data: collapsed.data, stackKeys: collapsed.stackKeys, sampleCounts } }
      }
    )
  )(state)
}

/**
 * Logs how many of the rendered stacked-bar series are sub-pixel in EVERY bucket (i.e. paint cost
 * with no visual payoff). A series counts as "visible" if it reaches at least [VISIBILITY_SHARE]
 * of some bucket's total. Diagnostic only — gated on the VizPerf flag, no behavioural effect.
 */
function reportSeriesVisibility(
  data: Array<Record<string, number | string>>,
  stackKeys: string[],
  level: number
): void {
  const bucketTotals = data.map((row) => {
    let t = 0
    for (const k of stackKeys) t += (row[k] as number) ?? 0
    return t
  })

  let visible = 0
  for (const k of stackKeys) {
    let isVisible = false
    for (let b = 0; b < data.length; b++) {
      const total = bucketTotals[b]!
      const v = (data[b]![k] as number) ?? 0
      if (total > 0 && v / total >= VISIBILITY_SHARE) {
        isVisible = true
        break
      }
    }
    if (isVisible) visible++
  }

  const total = stackKeys.length
  const invisible = total - visible
  const pct = total > 0 ? Math.round((invisible / total) * 100) : 0
  console.debug(
    `[viz-perf] stackedBar level=${level}: ${total} series → ${visible} visible, ` +
      `${invisible} sub-pixel (${pct}% wasted, <${VISIBILITY_SHARE * 100}% of every bucket)`
  )
}
